import {
    useState,
    CSSProperties,
} from 'react';
import MainContentContainer from 'components/shareable/MainContentContainer';
import DesignTab from './DesignTab';
import ConfirmationTab from './ConfirmationTab';
import SchedulingTab from './SchedulingTab';
import PostCreation from './PostCreation';
import isEmpty from 'lodash/isEmpty';
import { BLANK_CANVAS } from '../../utils';
import TopBar from 'components/shareable/TopBar/TopBar';
import { useHistory } from 'react-router-dom';
import useScreen from 'Hooks/useScreen';
import useMessageYourFans from 'Hooks/useMessageYourFans';
import Sty from 'modules/Sty';
import TabsSymphony, { TabItem } from 'elements/TabsSymphony';
import PrevNextFooter from 'elements/PrevNextFooter';
import TabSymphony from 'elements/TabSymphony';
import PageSymphony from 'elements/PageSymphony';
import CardSymphony from 'elements/CardSymphony';
import Icon from 'elements/Icon';

export enum STEPS {
    DESIGN = 'design',
    SCHEDULING = 'scheduling',
    CONFIRMATION = 'confirmation',
}

const { DESIGN, SCHEDULING, CONFIRMATION } = STEPS;

const messageYourFansTabs = [
    { id: DESIGN, title: 'Design' },
    { id: SCHEDULING, title: 'Scheduling' },
    { id: CONFIRMATION, title: 'Confirmation' },
];

const viewTitles = {
    FINALIZING: 'Finalizing your campaign...',
    MESSAGE_SENT: 'Your message was sent!',
    MESSAGE_SCHEDULED: 'Your message is scheduled!',
    MESSAGE_YOUR_FANS: 'Message your fans',
    ERROR: 'Error setting up your campaign...',
};

enum PostCreationStatus {
    FINALIZING = 'FINALIZING',
    MESSAGE_SENT = 'MESSAGE_SENT',
    MESSAGE_SCHEDULED = 'MESSAGE_SCHEDULED',
    MESSAGE_YOUR_FANS = 'MESSAGE_YOUR_FANS',
    ERROR = 'ERROR',
}

const { MESSAGE_YOUR_FANS } = PostCreationStatus;

const MessageYourFans = () => {
    const history = useHistory();
    const { mobileView, mStyle } = useScreen();
    const { design, details } = useMessageYourFans();

    /** Get the initial tab from URL query parameter or default to first tab */
    const getInitialTab = () => {
        const params = new URLSearchParams(window.location.search);
        const tabFromUrl = params.get('tab');
        return messageYourFansTabs.find(tab => tab.id === tabFromUrl) || messageYourFansTabs[0];
    };

    const [currentMessageYourFansTab, setCurrentMessageYourFansTab] = useState<TabItem>(getInitialTab());
    const [showPostCreation, setShowPostCreation] = useState(false);
    const [viewStatus, setViewStatus] = useState(MESSAGE_YOUR_FANS);

    const { subjectLine, emailTemplate } = design;
    const { date, scheduled } = details;

    const handleViewStatus = (status: PostCreationStatus) => {
        setViewStatus(status);
    };

    const handleClickBackButton = () => {
        let nextTab;
        if (currentMessageYourFansTab.id === CONFIRMATION) {
            nextTab = messageYourFansTabs[1];
        } else if (currentMessageYourFansTab.id === messageYourFansTabs[1].id) {
            nextTab = messageYourFansTabs[0];
        }

        if (nextTab) {
            const searchParams = new URLSearchParams(window.location.search);
            searchParams.set('tab', nextTab.id);
            history.replace({ search: searchParams.toString() });
            setCurrentMessageYourFansTab(nextTab);
        } else {
            history.push('/fans');
        }
    };

    const handleClickNextButton = () => {
        const isDesignTabDataSaved =
            !!emailTemplate &&
            emailTemplate !== BLANK_CANVAS &&
            !isEmpty(subjectLine) &&
            emailTemplate.length > 5;

        const isSchedulingTabDataSaved = scheduled === 'SCHEDULED' ? !!date : true;

        if (currentMessageYourFansTab.id === DESIGN && isDesignTabDataSaved) {
            const nextTab = messageYourFansTabs[1];
            const searchParams = new URLSearchParams(window.location.search);
            searchParams.set('tab', nextTab.id);
            history.replace({ search: searchParams.toString() });
            setCurrentMessageYourFansTab(nextTab);
        } else if (currentMessageYourFansTab.id === messageYourFansTabs[1].id && isSchedulingTabDataSaved) {
            const nextTab = messageYourFansTabs[2];
            const searchParams = new URLSearchParams(window.location.search);
            searchParams.set('tab', nextTab.id);
            history.replace({ search: searchParams.toString() });
            setCurrentMessageYourFansTab(nextTab);
        } else if (currentMessageYourFansTab.id === messageYourFansTabs[2].id) {
            setShowPostCreation(true);
        }
    };

    /** Update URL and state when tab changes */
    const handleTabChange = (tab: TabItem) => {
        const searchParams = new URLSearchParams(window.location.search);
        searchParams.set('tab', tab.id);
        history.replace({ search: searchParams.toString() });
        setCurrentMessageYourFansTab(tab as TabItem);
    };

    const renderMobileErrorMessage = () => {
        return (
            <CardSymphony className="mobileMessageYourFans" style={sty.mobileMessageYourFans}>
                <Icon className="iconComputerError" icon="computer" style={sty.iconComputerError} />
                <div style={Sty.text18bold}>
                    Design Emails with More Space
                </div>
                <div style={Sty.text14grey}>
                    The email creator requires a larger screen for advanced editing tools. Open this feature on a desktop or laptop to get started.
                </div>
            </CardSymphony>
        )
    }

    const renderMessageYourFansMiddleContent = () => {
        return (
            <PageSymphony
                className="messageYourFansContent"
                isStepper
                isContent
            >
                {mobileView ?
                    renderMobileErrorMessage()
                    :
                    <>
                        <TabSymphony
                            className="messageYourFansTab"
                            visible={currentMessageYourFansTab.id === DESIGN}
                        >
                            <DesignTab />
                        </TabSymphony>
                        <TabSymphony
                            className="messageYourFansTab"
                            visible={currentMessageYourFansTab.id === SCHEDULING}
                        >
                            <SchedulingTab />
                        </TabSymphony>
                        {currentMessageYourFansTab.id === CONFIRMATION &&
                            <TabSymphony
                                className="messageYourFansTab"
                                visible
                            >
                                <ConfirmationTab />
                            </TabSymphony>
                        }
                    </>
                }
            </PageSymphony>
        )
    };

    if (showPostCreation) {
        return (
            <MainContentContainer>
                <PostCreation
                    handleViewStatus={handleViewStatus}
                />
            </MainContentContainer>
        );
    }

    return (
        <div
            className="messageYourFansComponent"
            style={sty.messageYourFansComponent}
        >
            <TopBar
                className="messageYourFansTopBar"
                title={viewTitles[viewStatus]}
                isCloseIcon
                handleClose={() => history.push('/fans')}
            />
            <TabsSymphony
                className="messageYourFansTabs"
                tabs={messageYourFansTabs}
                selectedTab={currentMessageYourFansTab}
                onChange={(tab) => handleTabChange(tab as TabItem)}
                type="numbers"
                style={{ maxWidth: 540 }}
                isHeader
            />

            {renderMessageYourFansMiddleContent()}

            <PrevNextFooter
                className="messageYourFansPrevNextFooter"
                tabs={messageYourFansTabs}
                currentTab={currentMessageYourFansTab}
                handlePrevTab={handleClickBackButton}
                handleNextTab={handleClickNextButton}
                nextButtonLabel={currentMessageYourFansTab.id === CONFIRMATION ? 'Send Message' : 'Next'}
            />
        </div>
    );
};

const sty: Record<string, CSSProperties> = {
    messageYourFansComponent: {
        display: 'flex',
        flexDirection: 'column',
        width: '100%',
    },
    iconComputerError: {
        padding: 12,
        width: 64,
        height: 64,
        backgroundColor: '#EDECF2',
        filter: 'grayscale(100%) brightness(1.2) contrast(0.8)',
        borderRadius: 100,
        marginBottom: 16,
        marginTop: 16
    },
    messageYourFansContent: {
        alignSelf: 'center',
        paddingTop: 24,
        paddingBottom: 24,
    },
    mobileMessageYourFans: {
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        height: '100%',
        backgroundColor: 'white',
        paddingBottom: 48
    }
};

export default MessageYourFans; 